/**
 * Shared export configuration — single source of truth for
 * content options, preview descriptions, and column sets.
 *
 * Both the dialog UI and the actual generators consume this so
 * they can never drift out of sync.
 */

export interface ExportContentOptions {
  /** Include per-employee line-item breakdown (earnings + deductions rows) */
  includeLineItems: boolean;
  /** Include year-to-date summary section */
  includeYTD: boolean;
  /** Include hours breakdown columns (regular, overtime, holiday, paid leave) */
  includeHoursBreakdown: boolean;
}

export const DEFAULT_EXPORT_OPTIONS: ExportContentOptions = {
  includeLineItems: true,
  includeYTD: false,
  includeHoursBreakdown: true,
};

// ── CSV column descriptors ────────────────────────────────────

interface CsvColumnDescriptor {
  label: string;
  /** dot-path is only used for preview; actual rendering lives in the generator */
  group: 'identity' | 'hours' | 'pay' | 'lineItems' | 'ytd';
}

const BASE_CSV_COLUMNS: CsvColumnDescriptor[] = [
  { label: 'Employee Name', group: 'identity' },
  { label: 'Employee ID', group: 'identity' },
];

const HOURS_CSV_COLUMNS: CsvColumnDescriptor[] = [
  { label: 'Regular Hours', group: 'hours' },
  { label: 'Overtime Hours', group: 'hours' },
  { label: 'Holiday Hours', group: 'hours' },
  { label: 'Paid Leave Hours', group: 'hours' },
];

const PAY_CSV_COLUMNS: CsvColumnDescriptor[] = [
  { label: 'Gross Pay', group: 'pay' },
  { label: 'Total Deductions', group: 'pay' },
  { label: 'Net Pay', group: 'pay' },
];

const YTD_CSV_COLUMNS: CsvColumnDescriptor[] = [
  { label: 'YTD Gross', group: 'ytd' },
  { label: 'YTD Net', group: 'ytd' },
  { label: 'YTD Hours', group: 'ytd' },
  { label: 'YTD Periods', group: 'ytd' },
];

export function getCsvColumns(opts: ExportContentOptions): string[] {
  const cols = [...BASE_CSV_COLUMNS];
  if (opts.includeHoursBreakdown) cols.push(...HOURS_CSV_COLUMNS);
  cols.push(...PAY_CSV_COLUMNS);
  if (opts.includeYTD) cols.push(...YTD_CSV_COLUMNS);
  return cols.map(c => c.label);
}

// ── PDF section descriptors ───────────────────────────────────

export function getPdfSections(opts: ExportContentOptions): string[] {
  const sections = ['Employee header & pay period', 'Pay summary (Gross / Deductions / Net / Hours)'];
  if (opts.includeLineItems) sections.push('Earnings & deductions line items');
  if (opts.includeYTD) sections.push('Year-to-date summary');
  sections.push('DRAFT watermark');
  return sections;
}

// ── Unified preview descriptor ────────────────────────────────

export interface ExportPreviewDescriptor {
  title: string;
  description: string;
  columns: string[];
}

export function getExportPreview(
  format: 'csv' | 'pdf' | 'xlsx',
  opts: ExportContentOptions,
  employeeCount: number,
): ExportPreviewDescriptor | null {
  switch (format) {
    case 'csv':
      return {
        title: 'CSV Spreadsheet Export',
        description: `Comma-separated values file with ${opts.includeLineItems ? 'detailed' : 'summary'} payroll data`,
        columns: getCsvColumns(opts),
      };
    case 'pdf':
      return {
        title: 'PDF Payslip Export',
        description: `Individual draft payslips for ${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`,
        columns: getPdfSections(opts),
      };
    case 'xlsx':
      return {
        title: 'Excel Workbook Export',
        description: 'Multi-sheet Excel file (coming soon)',
        columns: [],
      };
    default:
      return null;
  }
}
